import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';


const steps = [
    {
        id: 'step-1',
        title: '1. Park Both Cars Safely',
        paragraphs: [
            'Pull the working car close enough for the jumper cables to reach, but do not let the vehicles touch. Set both cars in park or neutral, switch on the parking brake, and keep the area clear.',
            'That sounds basic, but this is where people usually rush because they just want the dead car running again.'
        ]
    },
    {
        id: 'step-2',
        title: '2. Shut Off Both Vehicles First',
        paragraphs: [
            'Before you start clipping cables anywhere, turn both engines off and switch off lights, AC, and anything else pulling power.',
            'It keeps the process cleaner and lowers the chance of making a bad connection under load.'
        ]
    },
    {
        id: 'step-3',
        title: '3. Connect the Red Cable to the Positive Terminals',
        paragraphs: [
            'Clip one red clamp to the positive terminal on the dead battery first. Then connect the other red clamp to the positive terminal on the good battery.',
            'Take a second here and actually look for the plus sign. This is not the step to guess on.'
        ]
    },
    {
        id: 'step-4',
        title: '4. Connect the Black Cable the Right Way',
        paragraphs: [
            'Connect one black clamp to the negative terminal on the good battery. The last black clamp should go to an unpainted metal ground point on the dead car, not straight to the dead battery if you can avoid it.',
            'That last ground point matters because it is the safer way to finish the circuit.'
        ]
    },
    {
        id: 'step-5',
        title: '5. Start the Working Car and Let It Run',
        paragraphs: [
            'Start the donor car and let it idle for a few minutes. Give the weak battery a little time before trying to crank the dead car right away.',
            'A lot of people skip the wait and wonder why the dead car still struggles.'
        ]
    },
    {
        id: 'step-6',
        title: '6. Try Starting the Dead Car',
        paragraphs: [
            'Now try to start the car with the dead battery. If it does not fire up on the first try, wait another couple of minutes and try again.',
            'If it still will not start after a couple of proper attempts, stop there. Repeated cranking is usually a sign there is more going on than just a weak battery.'
        ]
    },
    {
        id: 'step-7',
        title: '7. Remove the Cables in Reverse Order',
        paragraphs: [
            'Once the dead car starts, take the cables off in reverse order: black clamp from the grounded metal point, black from the good battery, red from the good battery, then red from the revived car.',
            'Do not let the clamps touch while you are removing them.'
        ]
    }
];

const faqs = [
    {
        question: 'What is the safest order to connect jumper cables?',
        answer: 'Red to dead battery positive, red to good battery positive, black to good battery negative, and the final black clamp to a metal ground point on the dead car.'
    },
    {
        question: 'Can I connect the last black clamp to the dead battery?',
        answer: 'It is better to use a bare metal ground point on the dead car when possible. That lowers the risk around the battery area.'
    },
    {
        question: 'How long should I let the donor car run before trying the dead car?',
        answer: 'A few minutes is usually enough to give the weak battery some help before you try starting the dead car.'
    },
    {
        question: 'What if the car still does not start after a jump attempt?',
        answer: 'Stop and recheck the connections. If the cables are correct and it still will not start, the battery may be fully dead or another issue like the starter or alternator may be involved.'
    },
    {
        question: 'Should I drive the car after it starts?',
        answer: 'Yes. Let it run and drive it for a while if it is safe to do so, but also get the battery and charging system checked if this was not a one-time fluke.'
    }
];

const Blog9 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 9;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            How to Jump Start a Car Safely: Step-by-Step Guide
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            If your battery dies, the main thing is not to panic and not to rush the cable order. A jump start is pretty straightforward when you do it calmly, but a sloppy connection can create bigger problems than the dead battery you started with.
                        </p>

                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 8 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/9_1.jpeg"
                            alt="Car battery and jumper cable safety before a jump start"
                            title="How to Jump Start a Car Safely"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: car_battery_warning_signs_featured.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#safety-summary" className="text-gray-900 no-underline hover:underline">Safety summary</a></li>
                            <li><a href="#before-you-start" className="text-gray-900 no-underline hover:underline">Before you start</a></li>
                            <li><a href="#step-1" className="text-gray-900 no-underline hover:underline">Park both cars safely</a></li>
                            <li><a href="#step-3" className="text-gray-900 no-underline hover:underline">Connect the red cables</a></li>
                            <li><a href="#step-4" className="text-gray-900 no-underline hover:underline">Connect the black cables</a></li>
                            <li><a href="#step-6" className="text-gray-900 no-underline hover:underline">Start the dead car</a></li>
                            <li><a href="#mistakes" className="text-gray-900 no-underline hover:underline">Common mistakes</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">Get help from CarFido</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            Most jump-start mistakes happen because people are in a hurry. The car will not start, traffic is moving, somebody is waiting, and now everyone is clipping cables on without slowing down for ten seconds.
                        </p>
                        <p className="mb-[18px] text-gray-700">
                            The safest approach is simple: confirm the terminals, connect in the right order, and stop if the battery looks swollen, cracked, or leaking.
                        </p>
                        <p className="mb-0 text-gray-700">
                            This guide walks through the safe way to do it so you can get moving again without frying something electrical in the process.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>How do you jump start a car safely?</strong><br />
                            Park both cars without letting them touch, connect red clamps to both positive terminals, connect the black clamp to the good battery and then to a metal ground point on the dead car, start the good car first, then start the dead car, and remove the cables in reverse order.
                        </p>
                    </section>

                    <section id="safety-summary" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Jump Start Safety Summary</h2>
                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Step</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">What To Remember</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Car position</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Keep both cars close enough for the cables, but never touching</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Red cable order</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Positive on dead battery first, then positive on the good battery</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Black cable order</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Negative on the good battery, then a metal ground point on the dead car</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium text-gray-900">When to stop</td>
                                        <td className="py-3.5 px-4 text-gray-600">If the battery is cracked, leaking, swollen, or the car still will not start after proper attempts</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="before-you-start" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Before You Start</h2>
                        <p className="mb-4 text-gray-700">
                            Before you connect anything, make sure both vehicles use the same basic battery voltage and that your jumper cables are in decent shape. Frayed or damaged cables are not worth gambling with.
                        </p>
                        <p className="mb-5 text-gray-700">
                            Also, if the battery case looks swollen or damaged, do not treat it like a normal jump-start situation. That is when it makes more sense to stop and get proper help.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Use a working donor car with a healthy battery</li>
                            <li>Keep metal tools and jewelry away from the battery area</li>
                            <li>Confirm the positive and negative terminals before clipping anything</li>
                            <li>Do not jump a battery that looks cracked, leaking, or badly swollen</li>
                        </ul>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/9_2.jpeg"
                            alt="Weak battery symptoms before a jump start"
                            title="Weak battery signs before jump starting"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: car_battery_slow_start_signs.png
                        </figcaption>
                    </figure>

                    {steps.slice(0, 5).map((step) => (
                        <section key={step.id} id={step.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {step.title}
                            </h2>
                            {step.paragraphs.map((paragraph, index) => (
                                <p key={index} className="mb-4 text-gray-700">
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <figure className="mb-10">
                        <img
                            src="/blogs/9_3.jpeg"
                            alt="Battery terminal corrosion and connection problems"
                            title="Battery terminal corrosion and connection checks"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: car_battery_terminal_corrosion.png
                        </figcaption>
                    </figure>

                    {steps.slice(5).map((step) => (
                        <section key={step.id} id={step.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {step.title}
                            </h2>
                            {step.paragraphs.map((paragraph, index) => (
                                <p key={index} className="mb-4 text-gray-700">
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <section id="mistakes" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Common Mistakes To Avoid</h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Guessing which terminal is positive or negative</li>
                            <li>Letting the cars touch each other</li>
                            <li>Connecting the last black clamp in the wrong place</li>
                            <li>Cranking over and over without rechecking the setup</li>
                            <li>Ignoring obvious battery damage and trying anyway</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">Get Help from CarFido</h2>

                            <p className="mb-3.5 text-gray-700">
                                If the car needed a jump once, that does not automatically mean the battery alone is the problem. Sometimes it is age, sometimes it is corrosion, and sometimes the charging system is not doing its job.
                            </p>

                            <p className="mb-3.5 text-gray-700">
                                CarFido helps you think through why the car died in the first place so you are not stuck doing the same jump-start again next week.
                            </p>

                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">You can ask questions such as:</p>

                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Why does my battery keep dying after I jump it?</li>
                                <li>Could a bad alternator be the real reason the car will not stay charged?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">Frequently Asked Questions</h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Final Thoughts</h2>
                        <p className="mb-4 text-gray-700">
                            Jump starting a car is not hard, but the order matters. If you stay calm and connect things the right way, it is usually a quick job.
                        </p>
                        <p className="mb-4 text-gray-700">
                            The bigger thing to remember is this: a jump start gets you moving again, but it does not explain why the battery died in the first place.
                        </p>
                        <p className="mb-0 text-gray-700">
                            If this keeps happening, check the battery, the terminals, and the charging system before it turns into a habit.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#safety-summary" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Safety summary</a></li>
                            <li><a href="#before-you-start" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Before you start</a></li>
                            <li><a href="#step-3" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Red cable order</a></li>
                            <li><a href="#step-4" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Black cable order</a></li>
                            <li><a href="#step-6" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Start the dead car</a></li>
                            <li><a href="#mistakes" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Common mistakes</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog9;
